import React from 'react';
import { pathFor, type RouteName } from '../../routes/routesConfig';
import './CustomerStoriesList.css';

/**
 * Customer stories on the landing pages, in the blog's layout
 * (climapri.fi/fi/blogi): heavy Inter heading, photo next to the text,
 * "<Asiakastarina> · <aihe>" label in the topic colour, bold title and a
 * date / reading-time row. Rendered by CustomerStorySection for every
 * language except Polish, which keeps its card layout.
 *
 * Titles are the stories' own titles (blog.posts.*.title in the locale files);
 * dates and reading times as on the Finnish blog page.
 */
type ListLang = 'fi' | 'en' | 'sv' | 'da' | 'de';

interface Copy {
  heading: string;
  all: string;
  read: string;
  kind: string;
  water: string;
  heat: string;
  titles: [string, string];
  locale: string;
}

const COPY: Record<ListLang, Copy> = {
  fi: {
    heading: 'Mitattuja tuloksia oikeista kohteista',
    all: 'Kaikki asiakastarinat',
    read: 'Lue tarina',
    kind: 'Asiakastarina',
    water: 'Vedenmittaus',
    heat: 'Lämmönohjaus',
    titles: ['Avara vähensi vedenkulutusta 20 %', 'Ikaalisten Kiinteistökonsultit vähensi kaukolämmön kulutusta 10 %'],
    locale: 'fi-FI',
  },
  en: {
    heading: 'Measured results from real buildings',
    all: 'All customer stories',
    read: 'Read the story',
    kind: 'Customer story',
    water: 'Water metering',
    heat: 'Heating control',
    titles: ['Avara cut water consumption by 20%', 'Ikaalisten Kiinteistökonsultit cut district heating consumption by 10%'],
    locale: 'en-GB',
  },
  sv: {
    heading: 'Uppmätta resultat från verkliga fastigheter',
    all: 'Alla kundberättelser',
    read: 'Läs berättelsen',
    kind: 'Kundberättelse',
    water: 'Vattenmätning',
    heat: 'Värmestyrning',
    titles: ['Avara minskade vattenförbrukningen med 20 %', 'Ikaalisten Kiinteistökonsultit minskade fjärrvärmeförbrukningen med 10 %'],
    locale: 'sv-SE',
  },
  da: {
    heading: 'Målte resultater fra rigtige ejendomme',
    all: 'Alle kundehistorier',
    read: 'Læs historien',
    kind: 'Kundehistorie',
    water: 'Vandmåling',
    heat: 'Varmestyring',
    titles: ['Avara sænkede vandforbruget med 20 %', 'Ikaalisten Kiinteistökonsultit sænkede fjernvarmeforbruget med 10 %'],
    locale: 'da-DK',
  },
  de: {
    heading: 'Gemessene Ergebnisse aus echten Gebäuden',
    all: 'Alle Kundenstorys',
    read: 'Story lesen',
    kind: 'Kundenstory',
    water: 'Wassermessung',
    heat: 'Heizungssteuerung',
    titles: ['Avara senkte den Wasserverbrauch um 20 %', 'Ikaalisten Kiinteistökonsultit senkte den Fernwärmeverbrauch um 10 %'],
    locale: 'de-DE',
  },
};

const STORIES: { route: RouteName; topic: 'vedenmittaus' | 'lammonohjaus'; date: string; readMin: number; photo: string; alt: string }[] = [
  { route: 'avaraWaterCase', topic: 'vedenmittaus', date: '2026-06-30', readMin: 6, photo: '/img/clients/Tuomas%20kuva%202026%204.webp', alt: 'Tuomas Rantsi, Avara' },
  { route: 'ikaalinenHeatingCase', topic: 'lammonohjaus', date: '2026-09-16', readMin: 6, photo: '/img/clients/Valtteri%20Holmala.webp', alt: 'Valtteri Holmala, Ikaalisten Kiinteistökonsultit Oy' },
];

const Arrow: React.FC = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3 8h9M8.5 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CustomerStoriesList: React.FC<{ lang: string; railClear?: boolean }> = ({ lang, railClear }) => {
  const l: ListLang = lang in COPY ? (lang as ListLang) : 'en';
  const c = COPY[l];
  // The Finnish blog filters by topic from the hash; the other blogs have no filter.
  const allHref = `${pathFor('blog', l)}${l === 'fi' ? '#asiakastarinat' : ''}`;
  const fmt = new Intl.DateTimeFormat(c.locale, { day: 'numeric', month: 'numeric', year: 'numeric', timeZone: 'UTC' });
  return (
    // id + data-rail-clear: the fi/en landing page's step rail hides over this section.
    <section
      className="cps"
      lang={l}
      id={l === 'fi' ? 'asiakastarina' : undefined}
      data-rail-clear={railClear ? '' : undefined}
      aria-labelledby="cps-title"
    >
      <div className="cps-wrap">
        <div className="cps-head">
          <h2 className="cps-title" id="cps-title">{c.heading}</h2>
          <a className="cps-all" href={allHref}>{c.all} <Arrow /></a>
        </div>
        <div className="cps-grid">
          {STORIES.map((s, i) => (
            <a key={s.route} className={`cps-item tc-${s.topic}`} href={pathFor(s.route, l)}>
              <div className="cps-photo">
                <img src={s.photo} alt={s.alt} loading="lazy" decoding="async" />
              </div>
              <div className="cps-body">
                <p className="cps-cat">{c.kind} · <span>{s.topic === 'vedenmittaus' ? c.water : c.heat}</span></p>
                <h3>{c.titles[i]}</h3>
                <div className="cps-meta">
                  <span>{fmt.format(new Date(s.date))}</span>
                  <span>{s.readMin} min</span>
                  <span className="go">{c.read} <Arrow /></span>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CustomerStoriesList;
